import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  IconButton,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
  AutoAwesome as AutoAwesomeIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { terminalColors } from '../utils/terminalColors';
import { AuditSeries, aiBuilderAPI } from '../services/api';
import { supabase } from '../lib/supabaseClient';
import TemplateQuestionsEditor, { Question, QuestionOption } from '../components/shared/TemplateQuestionsEditor';
import TemplateAIAssistPanel from '../components/shared/TemplateAIAssistPanel';
import LoadingModal from '../components/LoadingModal';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface TemplateKPI {
  id?: number;
  kpi_name: string;
  kpi_description: string;
  display_order?: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TemplateEditorPage: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine if we're in read-only mode based on URL path
  const isReadOnly = location.pathname.includes('/view/');
  
  // Check if we should show KPI tab (from AI Builder)
  const showKPITab = (location.state as any)?.showKPITab;
  
  // State management
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [template, setTemplate] = useState<AuditSeries | null>(null);
  const [currentTab, setCurrentTab] = useState(showKPITab ? 2 : 0); // Start on KPI tab if redirected from AI Builder
  const [error, setError] = useState('');
  
  // Template basic info states
  const [keyFocusAreas, setKeyFocusAreas] = useState<string[]>([]);
  const [newFocusArea, setNewFocusArea] = useState('');
  
  // Questions management states
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [assistLocksEditor, setAssistLocksEditor] = useState(false);
  
  // KPI management states
  const [kpis, setKpis] = useState<TemplateKPI[]>([]);
  const [kpisLoading, setKpisLoading] = useState(false);
  const [kpisRegenerating, setKpisRegenerating] = useState(false);
  const [editingKPI, setEditingKPI] = useState<TemplateKPI | null>(null);
  const [kpiDialog, setKpiDialog] = useState(false);
  const [regenerateConfirmDialog, setRegenerateConfirmDialog] = useState(false);

  useEffect(() => {
    loadTemplate();
  }, [templateId]);

  const loadTemplate = async () => {
    if (!templateId) return;
    
    try {
      setLoading(true);
      console.log(terminalColors.info('🔄 Loading template details...'));
      
      const templateData = await aiBuilderAPI.getTemplate(parseInt(templateId));
      setTemplate(templateData);
      
      // Parse key focus areas
      if (templateData.key_focus_areas) {
        const areas = templateData.key_focus_areas
          .split('\n')
          .filter(area => area.trim())
          .map(area => area.trim());
        setKeyFocusAreas(areas);
      }
      
      console.log(terminalColors.success('✅ Template loaded successfully'));
      
      // Load questions for this template
      await loadQuestions();
      await loadKPIs();
    } catch (error: any) {
      console.error(terminalColors.error('❌ Error loading template:'), error);
      setError('Failed to load template. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    if (!templateId) return;
    
    try {
      setQuestionsLoading(true);
      console.log(terminalColors.info('🔄 Loading questions...'));
      
      const questionsData = await aiBuilderAPI.getTemplateQuestions(parseInt(templateId));
      setQuestions(questionsData.questions || []);
      
      console.log(terminalColors.success(`✅ Loaded ${questionsData.questions?.length || 0} questions`));
    } catch (error: any) {
      console.error(terminalColors.error('❌ Error loading questions:'), error);
      // Don't set error state for questions - just log it
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!template || !templateId) return;
    
    try {
      setSaving(true);
      console.log(terminalColors.info('💾 Saving template changes...'));
      
      await aiBuilderAPI.updateTemplate(parseInt(templateId), template);
      
      console.log(terminalColors.success('✅ Template saved successfully'));
      setError('');
    } catch (error: any) {
      console.error(terminalColors.error('❌ Error saving template:'), error);
      setError('Failed to save template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishTemplate = async () => {
    if (!template || !templateId) return;
    
    try {
      setPublishing(true);
      console.log(terminalColors.info('📢 Publishing template...'));
      
      // Call publish endpoint
      await aiBuilderAPI.publishTemplate(parseInt(templateId));
      
      console.log(terminalColors.success('✅ Template published successfully'));
      
      // Update local template status
      setTemplate(prev => ({
        ...prev!,
        status: 'published',
      }));
      
      setError('');
      
      // Navigate back to template management (Our Templates tab)
      navigate('/admin/templates');
      
    } catch (error: any) {
      console.error(terminalColors.error('❌ Error publishing template:'), error);
      setError('Failed to publish template. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  const handleTemplateChange = (field: keyof AuditSeries, value: any) => {
    if (!template) return;
    setTemplate(prev => ({
      ...prev!,
      [field]: value,
    }));
  };

  const addFocusArea = () => {
    if (newFocusArea.trim() && !keyFocusAreas.includes(newFocusArea.trim())) {
      const updatedAreas = [...keyFocusAreas, newFocusArea.trim()];
      setKeyFocusAreas(updatedAreas);
      handleTemplateChange('key_focus_areas', updatedAreas.join('\n'));
      setNewFocusArea('');
    }
  };

  const removeFocusArea = (index: number) => {
    const updatedAreas = keyFocusAreas.filter((_, i) => i !== index);
    setKeyFocusAreas(updatedAreas);
    handleTemplateChange('key_focus_areas', updatedAreas.join('\n'));
  };

  /**
   * Handle questions change from editor
   */
  const handleQuestionsChange = (updatedQuestions: Question[]) => {
    setQuestions(updatedQuestions);
  };

  /**
   * Save questions to backend
   */
  const saveQuestions = async () => {
    if (!templateId) return;
    
    try {
      console.log(terminalColors.info('💾 Saving questions...'));
      
      await aiBuilderAPI.updateTemplateQuestions(parseInt(templateId), questions);
      
      console.log(terminalColors.success('✅ Questions saved successfully'));
    } catch (error: any) {
      console.error(terminalColors.error('❌ Error saving questions:'), error);
      setError('Failed to save questions. Please try again.');
    }
  };

  // KPI Management Functions
  const loadKPIs = async () => {
    if (!templateId) return;
    
    try {
      setKpisLoading(true);
      console.log(terminalColors.info('🎯 Loading template KPIs...'));
      
      const API_BASE = import.meta.env.PROD 
        ? 'https://web-production-d0043.up.railway.app/api'
        : (import.meta.env.VITE_API_URL || 'http://localhost:8001/api');
      
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      const response = await fetch(`${API_BASE}/templates/${templateId}/kpis/`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to load KPIs');
      
      const data = await response.json();
      setKpis(data.kpis || []);
      
      console.log(terminalColors.success(`✅ Loaded ${data.kpis?.length || 0} KPIs`));
    } catch (error: any) {
      console.error(terminalColors.error('❌ Error loading KPIs:'), error);
    } finally {
      setKpisLoading(false);
    }
  };

  const handleSaveKPI = async () => {
    if (!editingKPI || !templateId) return;
    
    try {
      const API_BASE = import.meta.env.PROD 
        ? 'https://web-production-d0043.up.railway.app/api'
        : (import.meta.env.VITE_API_URL || 'http://localhost:8001/api');
      
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      if (editingKPI.id) {
        // Update existing KPI
        setKpis(prev => prev.map(k => k.id === editingKPI.id ? editingKPI : k));
      } else {
        // Add new KPI
        const response = await fetch(`${API_BASE}/templates/${templateId}/kpis/add/`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include',
          body: JSON.stringify({
            kpi_name: editingKPI.kpi_name,
            kpi_description: editingKPI.kpi_description,
            display_order: kpis.length
          })
        });
        
        if (!response.ok) throw new Error('Failed to save KPI');
        
        const data = await response.json();
        setKpis(prev => [...prev, data.mapping]);
        console.log(terminalColors.success('✅ KPI added successfully'));
      }
      
      setKpiDialog(false);
      setEditingKPI(null);
    } catch (error: any) {
      console.error(terminalColors.error('❌ Error saving KPI:'), error);
      setError('Failed to save KPI. Please try again.');
    }
  };

  const handleDeleteKPI = async (kpiId: number) => {
    if (!templateId) return;
    
    try {
      const API_BASE = import.meta.env.PROD 
        ? 'https://web-production-d0043.up.railway.app/api'
        : (import.meta.env.VITE_API_URL || 'http://localhost:8001/api');
      
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      const response = await fetch(`${API_BASE}/templates/${templateId}/kpis/${kpiId}/delete/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to delete KPI');
      
      setKpis(prev => prev.filter(k => k.id !== kpiId));
      console.log(terminalColors.success('✅ KPI deleted successfully'));
    } catch (error: any) {
      console.error(terminalColors.error('❌ Error deleting KPI:'), error);
      setError('Failed to delete KPI. Please try again.');
    }
  };

  const handleRegenerateKPIsConfirm = async () => {
    if (!templateId || !template) return;
    
    setRegenerateConfirmDialog(false);
    
    try {
      setKpisRegenerating(true);
      console.log(terminalColors.info('🤖 Regenerating KPIs with AI...'));
      
      const API_BASE = import.meta.env.PROD 
        ? 'https://web-production-d0043.up.railway.app/api'
        : (import.meta.env.VITE_API_URL || 'http://localhost:8001/api');
      
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      const response = await fetch(`${API_BASE}/templates/${templateId}/kpis/suggest/`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          template_name: template.name,
          key_focus_areas: template.key_focus_areas || '',
        })
      });
      
      if (!response.ok) throw new Error('Failed to generate KPIs');
      
      const data = await response.json();
      
      // Reload KPIs after generation
      await loadKPIs();
      
      console.log(terminalColors.success(`✅ Generated ${data.kpis?.length || 0} KPIs successfully`));
    } catch (error: any) {
      console.error(terminalColors.error('❌ Error regenerating KPIs:'), error);
      setError('Failed to regenerate KPIs. Please try again.');
    } finally {
      setKpisRegenerating(false);
    }
  };

  const iconOptions = [
    'business', 'assessment', 'security', 'factory', 'restaurant', 'local_shipping',
    'construction', 'account_balance', 'science', 'medical_services', 'engineering',
    'agriculture', 'store', 'school', 'apartment', 'flight', 'directions_car'
  ];

  // Map FontAwesome icon names to Material-UI icon names
  const mapIconName = (iconName: string): string => {
    const iconMap: { [key: string]: string } = {
      'fas fa-industry': 'factory',
      'fas fa-business': 'business',
      'fas fa-assessment': 'assessment',
      'fas fa-security': 'security',
      'fas fa-restaurant': 'restaurant',
      'fas fa-shipping': 'local_shipping',
      'fas fa-construction': 'construction',
      'fas fa-bank': 'account_balance',
      'fas fa-science': 'science',
      'fas fa-medical': 'medical_services',
      'fas fa-engineering': 'engineering',
      'fas fa-agriculture': 'agriculture',
      'fas fa-store': 'store',
      'fas fa-school': 'school',
      'fas fa-apartment': 'apartment',
      'fas fa-flight': 'flight',
      'fas fa-car': 'directions_car'
    };
    
    // If it's a FontAwesome icon, map it to Material-UI
    if (iconMap[iconName]) {
      console.log(`🔄 Mapping icon: ${iconName} → ${iconMap[iconName]}`);
      return iconMap[iconName];
    }
    
    // If it's already a Material-UI icon or unknown, return as-is or default
    const result = iconOptions.includes(iconName) ? iconName : 'business';
    if (result === 'business' && iconName !== 'business') {
      console.log(`⚠️ Unknown icon "${iconName}", defaulting to "business"`);
    }
    return result;
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading template...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (!template) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">
          Template not found or failed to load.
          <Button
            variant="outlined"
            size="small"
            onClick={() => navigate('/admin/templates')}
            sx={{ ml: 2 }}
          >
            Back to Templates
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Success Message from AI Builder */}
      {(location.state as any)?.message && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => navigate(location.pathname, { replace: true, state: {} })}>
          {(location.state as any).message}
        </Alert>
      )}
      
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={4}>
          <Box display="flex" alignItems="center" gap={2} sx={{ flex: 1, minWidth: 0 }}>
            <IconButton onClick={() => navigate('/admin/templates')}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                <Typography variant="h4" sx={{ whiteSpace: 'nowrap' }}>
                  {isReadOnly ? 'View Template:' : 'Edit Template:'}
                </Typography>
                {isReadOnly ? (
                  <Typography variant="h4" sx={{ minWidth: 0 }}>
                    {template.name}
                  </Typography>
                ) : (
                  <TextField
                    value={template.name || ''}
                    onChange={(e) => handleTemplateChange('name', e.target.value)}
                    variant="standard"
                    fullWidth
                    inputProps={{ 'aria-label': 'Template name' }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '2.125rem',
                        fontWeight: 400,
                        lineHeight: 1.235,
                      },
                    }}
                  />
                )}
                {template?.status === 'draft' && (
                  <Chip label="DRAFT" color="warning" size="small" />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {isReadOnly 
                  ? 'Read-only view of template questions and settings'
                  : template?.status === 'draft'
                  ? 'Complete and publish this draft to make it available in "Our Templates"'
                  : 'Configure template settings, questions, and answer options'
                }
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={2} sx={{ flexShrink: 0 }}>
            {isReadOnly ? (
              <Button
                variant="contained"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/admin/templates')}
              >
                Back to Templates
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={() => navigate('/admin/templates')}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={handleSaveTemplate}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                {/* Show Publish button if template is a draft */}
                {template?.status === 'draft' && (
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    startIcon={saving ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                    onClick={handlePublishTemplate}
                    disabled={saving}
                  >
                    {saving ? 'Publishing...' : 'Publish Template'}
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Main Content Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab label="Basic Information" />
          <Tab label="Questions & Answers" />
          <Tab label="KPIs Influenced" />
        </Tabs>

        {/* Basic Information Tab */}
        <TabPanel value={currentTab} index={0}>
          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
            <Box sx={{ flex: 2 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Template Details
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 2, mb: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <TextField
                      fullWidth
                      label="Template Name"
                      value={template.name || ''}
                      onChange={(e) => handleTemplateChange('name', e.target.value)}
                      disabled={isReadOnly}
                      sx={{ flex: 2 }}
                    />
                    
                    <FormControl sx={{ flex: 1, minWidth: 150 }}>
                      <InputLabel>Icon</InputLabel>
                      <Select
                        value={mapIconName(template.icon_name || 'business')}
                        onChange={(e) => handleTemplateChange('icon_name', e.target.value)}
                        disabled={isReadOnly}
                        label="Icon"
                      >
                        {iconOptions.map(icon => (
                          <MenuItem key={icon} value={icon}>
                            {icon.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Template Description"
                    value={template.description || ''}
                    onChange={(e) => handleTemplateChange('description', e.target.value)}
                    disabled={isReadOnly}
                    placeholder="Brief description of what this audit template covers..."
                    sx={{ mb: 2 }}
                    helperText="This description will be shown to users when browsing templates"
                  />

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Radar chart category level</InputLabel>
                    <Select
                      value={template.radar_category_level || 2}
                      onChange={(e) => handleTemplateChange(
                        'radar_category_level',
                        Number(e.target.value) as 1 | 2
                      )}
                      disabled={isReadOnly}
                      label="Radar chart category level"
                    >
                      <MenuItem value={1}>Level 1 headings</MenuItem>
                      <MenuItem value={2}>Level 2 headings</MenuItem>
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      Controls which question heading level is plotted on the results radar chart.
                    </Typography>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={template.is_shared_community || false}
                        onChange={(e) => handleTemplateChange('is_shared_community', e.target.checked)}
                        disabled={isReadOnly}
                      />
                    }
                    label="Share with Community"
                    sx={{ mb: 2 }}
                  />
                  
                  {template.is_ai_generated && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      This template was AI-generated on {template.ai_generation_date ? 
                        new Date(template.ai_generation_date).toLocaleDateString() : 'unknown date'
                      } with topic: "{template.ai_generation_topic}"
                    </Alert>
                  )}
                </CardContent>
              </Card>

              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Key Focus Areas
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Define priority areas for auditors to focus on first
                  </Typography>

                  {!isReadOnly && (
                    <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Add Focus Area"
                        value={newFocusArea}
                        onChange={(e) => setNewFocusArea(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addFocusArea()}
                        placeholder="e.g., Master Data Quality"
                        sx={{ flex: 1 }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addFocusArea}
                        disabled={!newFocusArea.trim()}
                      >
                        Add
                      </Button>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {keyFocusAreas.map((area, index) => (
                      <Chip
                        key={index}
                        label={area}
                        onDelete={isReadOnly ? undefined : () => removeFocusArea(index)}
                        deleteIcon={<DeleteIcon />}
                        variant="outlined"
                        color="primary"
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Template Statistics
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Usage Count
                      </Typography>
                      <Typography variant="h6">
                        {template.usage_count || 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Community Rating
                      </Typography>
                      <Typography variant="h6">
                        {template.community_rating ? template.community_rating.toFixed(1) : '0.0'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Created
                      </Typography>
                      <Typography variant="body2">
                        {template.created_at ? 
                          new Date(template.created_at).toLocaleDateString() : 'Unknown'
                        }
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </TabPanel>

        {/* Questions & Answers Tab */}
        <TabPanel value={currentTab} index={1}>
          {!isReadOnly && (
            <TemplateAIAssistPanel
              questions={questions}
              onQuestionsApplied={handleQuestionsChange}
              templateId={templateId ? parseInt(templateId, 10) : undefined}
              topic={template?.ai_generation_topic || template?.name}
              disabled={questionsLoading}
              onProposalActiveChange={setAssistLocksEditor}
              onPersistedApplied={async () => {
                await loadQuestions();
              }}
            />
          )}
          <TemplateQuestionsEditor
            questions={questions}
            onChange={handleQuestionsChange}
            isReadOnly={isReadOnly || assistLocksEditor}
            loading={questionsLoading}
            onSave={saveQuestions}
            showSaveButton={!assistLocksEditor}
          />
        </TabPanel>

        {/* KPIs Influenced Tab */}
        <TabPanel value={currentTab} index={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Box>
              <Typography variant="h6">
                KPIs Influenced ({kpis.length})
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Specify which Key Performance Indicators this audit template helps improve
              </Typography>
            </Box>
            {!isReadOnly && (
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={kpisRegenerating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                  onClick={() => setRegenerateConfirmDialog(true)}
                  disabled={kpisRegenerating}
                >
                  {kpisRegenerating ? 'Generating...' : 'Re-generate KPIs'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingKPI({ kpi_name: '', kpi_description: '' });
                    setKpiDialog(true);
                  }}
                >
                  Add KPI
                </Button>
              </Box>
            )}
          </Box>

          {kpisLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : kpis.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No KPIs Added Yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Add KPIs that this audit template will help improve. These will be used to match with company KPIs and help them find relevant templates.
                </Typography>
                {!isReadOnly && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setEditingKPI({ kpi_name: '', kpi_description: '' });
                      setKpiDialog(true);
                    }}
                  >
                    Add First KPI
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <List>
              {kpis.map((kpi, index) => (
                <Card key={kpi.id || index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Typography variant="h6" gutterBottom>
                          {kpi.kpi_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {kpi.kpi_description}
                        </Typography>
                      </Box>
                      {!isReadOnly && (
                        <Box display="flex" gap={1}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditingKPI(kpi);
                              setKpiDialog(true);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => kpi.id && handleDeleteKPI(kpi.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </List>
          )}
        </TabPanel>
      </Paper>

      {/* KPI Edit Dialog */}
      <Dialog
        open={kpiDialog}
        onClose={() => setKpiDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingKPI?.id ? 'Edit KPI' : 'Add KPI'}
        </DialogTitle>
        <DialogContent>
          {editingKPI && (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="KPI Name"
                value={editingKPI.kpi_name}
                onChange={(e) => setEditingKPI(prev => prev ? 
                  { ...prev, kpi_name: e.target.value } : null
                )}
                placeholder="e.g., Inventory Turnover Rate"
                required
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="KPI Description"
                value={editingKPI.kpi_description}
                onChange={(e) => setEditingKPI(prev => prev ? 
                  { ...prev, kpi_description: e.target.value } : null
                )}
                placeholder="Describe what this KPI measures and how this audit helps improve it"
                required
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveKPI}
            disabled={!editingKPI?.kpi_name.trim() || !editingKPI?.kpi_description.trim()}
          >
            Save KPI
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate KPIs Confirmation Dialog */}
      <Dialog
        open={regenerateConfirmDialog}
        onClose={() => setRegenerateConfirmDialog(false)}
        maxWidth="sm"
      >
        <DialogTitle>
          🤖 Re-generate KPIs with AI?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            This will replace all existing KPIs with AI-generated suggestions based on your template's name and focus areas.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Warning:</strong> This action will delete all current KPIs for this template.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenerateConfirmDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleRegenerateKPIsConfirm}
            startIcon={<AutoAwesomeIcon />}
            color="primary"
          >
            Generate KPIs
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Modals */}
      <LoadingModal
        open={saving}
        title="Saving Template"
        message="Saving your changes. This may take a moment."
      />
      
      <LoadingModal
        open={publishing}
        title="Publishing Template"
        message="Validating and publishing the template. This may take a few seconds."
      />
      
      <LoadingModal
        open={kpisRegenerating}
        title="Regenerating KPIs"
        message="AI is analyzing your template to generate relevant KPIs. This may take several seconds."
      />
    </Container>
  );
};

export default TemplateEditorPage; 